/**
 * Renders a markdown document as a readable page block. The design system's
 * only markdown renderer is `Chat.MessageMarkdown`, which throws outside a
 * `<Chat.Message>` and carries chat-bubble styling (`bg-black/15` code chips,
 * de-escalated headings) — wrong for a full document, so this is the standalone
 * one. See DESIGN-SYSTEM-CANDIDATES.md.
 *
 * `variant="policy"` styles the guardrail-catalog shape: `## {situation}`
 * headings in title case, and the rule lines under each heading as a
 * red-left-bordered block whose bold labels (**When helping with:** …) render
 * as quiet uppercase eyebrows.
 */
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

const BASE_CLASSES = [
  'text-ink space-y-3 text-sm leading-relaxed',
  '[&_h1]:text-ink [&_h1]:text-base [&_h1]:font-bold',
  '[&_h3]:text-ink [&_h3]:text-sm [&_h3]:font-semibold',
  '[&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5',
  '[&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5',
  '[&_code]:bg-neutral-soft [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.9em]',
  '[&_pre]:bg-neutral-soft [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:p-3',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_a]:text-link [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-link-hover',
  '[&_blockquote]:border-outline-strong [&_blockquote]:text-ink-muted [&_blockquote]:border-l-2 [&_blockquote]:pl-3',
  '[&_hr]:border-outline [&_hr]:my-4',
  // GFM tables: a fault-code map is the most likely table in the knowledge
  // base, and unstyled it reads as a wall of pipes.
  '[&_table]:w-full [&_table]:text-left [&_table]:border-collapse',
  '[&_th]:border-outline [&_th]:text-ink [&_th]:border-b [&_th]:px-2 [&_th]:py-1.5 [&_th]:font-semibold',
  '[&_td]:border-outline [&_td]:border-b [&_td]:px-2 [&_td]:py-1.5 [&_td]:align-top',
];

const DEFAULT_CLASSES = [
  '[&_h2]:text-ink [&_h2]:text-sm [&_h2]:font-bold [&_h2]:uppercase [&_h2]:tracking-wide',
  '[&_strong]:text-ink [&_strong]:font-semibold',
];

/** The guardrail-rule treatment: heading, then the labeled lines as a rule block. */
const POLICY_CLASSES = [
  '[&_h2]:text-ink [&_h2]:text-[15px] [&_h2]:font-bold [&_h2]:pt-2',
  '[&_strong]:text-ink [&_strong]:font-semibold',
  // The paragraph right under a rule heading = the WHEN/NEVER/INSTEAD lines.
  '[&_h2+p]:border-primary [&_h2+p]:border-l-2 [&_h2+p]:pl-4 [&_h2+p]:py-0.5 [&_h2+p]:leading-7',
  // Its bold labels become quiet uppercase eyebrows.
  '[&_h2+p_strong]:text-ink-muted [&_h2+p_strong]:text-xs [&_h2+p_strong]:font-bold [&_h2+p_strong]:uppercase [&_h2+p_strong]:tracking-wider',
];

interface MarkdownViewerProps {
  children: string;
  variant?: 'default' | 'policy';
  className?: string;
}

export const MarkdownViewer = ({
  children,
  variant = 'default',
  className = '',
}: MarkdownViewerProps) => (
  <div
    className={[
      ...BASE_CLASSES,
      ...(variant === 'policy' ? POLICY_CLASSES : DEFAULT_CLASSES),
      className,
    ].join(' ')}
  >
    {/* remark-breaks: policy documents are written by non-markdown authors —
        a single newline between rule lines must render as a line break, not
        collapse into one paragraph.
        remark-gfm: tables and strikethrough. A general-knowledge rule holding a
        fault-code table rendered as raw pipes without it. */}
    <ReactMarkdown remarkPlugins={[remarkBreaks, remarkGfm]}>{children}</ReactMarkdown>
  </div>
);
